import { useState } from 'react'
import { Stack, SimpleGrid, Text, Box, Group, Badge, Select, Table, Skeleton, Alert, Tooltip } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { securityApi } from '@/api/security'
import { WidgetCard } from '@/components/ui'
import { ScoreGauge } from '@/components/security/ScoreGauge'
import { SeverityBadge } from '@/components/security/SeverityBadge'
import { FindingDrawer } from '@/components/security/FindingDrawer'
import type { SecurityFinding } from '@/api/security'

const OWASP_CATEGORIES = [
  { id: 'A01', name: 'Broken Access Control' },
  { id: 'A02', name: 'Cryptographic Failures' },
  { id: 'A03', name: 'Injection' },
  { id: 'A04', name: 'Insecure Design' },
  { id: 'A05', name: 'Security Misconfiguration' },
  { id: 'A06', name: 'Vulnerable Components' },
  { id: 'A07', name: 'Auth Failures' },
  { id: 'A08', name: 'Data Integrity Failures' },
  { id: 'A09', name: 'Logging Failures' },
  { id: 'A10', name: 'SSRF' },
]

function getCategoryScoreColor(score: number | undefined): string {
  if (score == null) return '#868e96'
  if (score >= 80) return '#2f9e44'
  if (score >= 60) return '#f59f00'
  if (score >= 40) return '#f76707'
  return '#e03131'
}

export function TenantSecurityDetail() {
  const { tenantId: tenantIdStr } = useParams<{ tenantId: string }>()
  const tenantId = Number(tenantIdStr)
  const navigate = useNavigate()
  const [severityFilter, setSeverityFilter] = useState<string | null>(null)
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null)
  const [selectedFinding, setSelectedFinding] = useState<SecurityFinding | null>(null)
  const [drawerOpened, setDrawerOpened] = useState(false)

  const overview = useQuery({
    queryKey: ['security', 'overview', tenantId],
    queryFn: () => securityApi.getOverview(tenantId),
    enabled: !!tenantId,
  })

  const findings = useQuery({
    queryKey: ['security', 'findings', tenantId, severityFilter, categoryFilter],
    queryFn: () => securityApi.listFindings(tenantId, {
      severity: severityFilter ?? undefined,
      category: categoryFilter ?? undefined,
    }),
    enabled: !!tenantId,
  })

  if (!tenantId || isNaN(tenantId)) return <Alert color="red">Tenant ID inválido.</Alert>

  const data = overview.data
  const isLoading = overview.isLoading
  const latestScan = data?.latest_scans?.[0]
  const categoryScores = latestScan?.scores_by_category ?? {}

  return (
    <Stack gap="xl">
      {/* Score + Category Cards */}
      <Group align="flex-start" gap="xl">
        <WidgetCard accent="primary" p="md" style={{ minWidth: 180 }}>
          {isLoading ? (
            <Skeleton height={160} />
          ) : (
            <ScoreGauge score={data?.overall_score ?? 0} label="Overall Score" />
          )}
        </WidgetCard>

        <Box style={{ flex: 1 }}>
          <Text size="sm" fw={600} mb="sm">OWASP Categories</Text>
          <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }} spacing="sm">
            {OWASP_CATEGORIES.map((cat) => {
              const score = categoryScores[cat.id]
              const bgColor = getCategoryScoreColor(score)
              return (
                <Tooltip key={cat.id} label={cat.name} withArrow>
                  <Box
                    onClick={() => navigate(`/security/category/${cat.id}`)}
                    style={{
                      background: bgColor,
                      borderRadius: 8,
                      padding: '12px 8px',
                      cursor: 'pointer',
                      transition: 'transform 0.15s',
                      textAlign: 'center',
                      color: '#fff',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.03)' }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)' }}
                  >
                    <Text fw={700} size="sm">{cat.id}</Text>
                    <Text fw={700} size="lg">{score != null ? score : '--'}</Text>
                  </Box>
                </Tooltip>
              )
            })}
          </SimpleGrid>
        </Box>
      </Group>

      {/* Findings Table */}
      <WidgetCard>
        <Group justify="space-between" mb="md">
          <Text size="sm" fw={600}>Findings</Text>
          <Group gap="sm">
            <Select
              size="xs"
              placeholder="Severity"
              clearable
              value={severityFilter}
              onChange={setSeverityFilter}
              data={['critical', 'high', 'medium', 'low', 'info']}
              w={120}
            />
            <Select
              size="xs"
              placeholder="Category"
              clearable
              value={categoryFilter}
              onChange={setCategoryFilter}
              data={OWASP_CATEGORIES.map(c => ({ value: c.id, label: `${c.id} - ${c.name}` }))}
              w={220}
            />
            <Badge variant="light">{findings.data?.total ?? 0} findings</Badge>
          </Group>
        </Group>

        {findings.isLoading ? (
          <Skeleton height={300} />
        ) : (findings.data?.findings?.length ?? 0) > 0 ? (
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Severity</Table.Th>
                <Table.Th>Title</Table.Th>
                <Table.Th>Category</Table.Th>
                <Table.Th>Resource</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>First Seen</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {findings.data!.findings.map((f) => (
                <Table.Tr
                  key={f.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => { setSelectedFinding(f); setDrawerOpened(true) }}
                >
                  <Table.Td><SeverityBadge severity={f.severity} size="xs" /></Table.Td>
                  <Table.Td><Text size="sm" lineClamp={1}>{f.title}</Text></Table.Td>
                  <Table.Td><Badge size="xs" variant="light">{f.category}</Badge></Table.Td>
                  <Table.Td><Text size="xs" c="dimmed" lineClamp={1} ff="monospace">{f.resource_type || 'N/A'}</Text></Table.Td>
                  <Table.Td><Badge size="xs" variant="outline">{f.status}</Badge></Table.Td>
                  <Table.Td><Text size="xs" c="dimmed">{new Date(f.first_seen_at).toLocaleDateString()}</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        ) : (
          <Text size="sm" c="dimmed" ta="center" py="xl">Nenhum finding encontrado com os filtros selecionados.</Text>
        )}
      </WidgetCard>

      <FindingDrawer
        finding={selectedFinding}
        opened={drawerOpened}
        onClose={() => { setDrawerOpened(false); setSelectedFinding(null) }}
      />
    </Stack>
  )
}
